import React from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { grey } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { Box, IconButton, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ProposalProduct } from '@trnsact/trnsact-shared-types/dist/generated';
import { CardPriceBlock } from '../cardPriceBlock';
import { ProposalProductCardAddons } from './ProposalProductCardAddons';
import { ProposalProductsCardTypeChip } from '../ProposalProductsCardTypeChip';
import { ProductCatalogDetails } from '../catalogDetails/ProductCatalogDetails';
import { AftermarketProductTypes, DnDTypes, ProductConfig, ProposalProductCardModes } from '../../../types';
import { ProposalProductPricingValidationStatus } from './ProposalProductPricingValidationStatus';
import { ProposalProductCardDocuments } from './ProposalProductCardDocuments';
import { RemoveNoLongerAvailableProduct } from '../proposalProductsCards/products/RemoveNoLongerAvailableProduct';
import { RenderWithCondition } from '../../../../../components/shared/Conditional/RenderWithCondition';

interface Props {
  logo?: string;
  index: number;
  product: ProposalProduct;
  onRemoveProduct?: VoidFunction;
  mode?: ProposalProductCardModes;
  productConfiguration: ProductConfig;
  onRemoveAddon: (title: string) => void;
  onOpenProductDetailsDialog?: VoidFunction;
  onSwitchProducts?: (from: number, to: number) => void;
}

export const ProposalProductCard = ({
  logo,
  index,
  product,
  onRemoveAddon,
  onRemoveProduct,
  onSwitchProducts,
  productConfiguration,
  onOpenProductDetailsDialog,
  mode = ProposalProductCardModes.Desking,
}: Props) => {
  const classes = useStyles();

  const displayTitle = (() => {
    if (
      product.aftermarketProduct?.aftermarketVendorApiChannel !== AftermarketProductTypes.TruckMasterBuckeye ||
      !productConfiguration?.subName
    ) {
      return product.title;
    }
    // Guard against doubling when the drag item already has the formatted title (e.g. dragged from the full card)
    if (product.title.includes(productConfiguration.subName)) {
      return product.title;
    }
    return `${product.title} ${productConfiguration.subName}${productConfiguration?.buckeyeConstructorTermMonths ? ` (Term: ${productConfiguration.buckeyeConstructorTermMonths} Months)` : ''}`;
  })();

  const [, ref] = useDrag({
    type: DnDTypes.ProductInMenu,
    item: { index },
  });

  const [, drop] = useDrop({
    accept: DnDTypes.ProductInMenu,
    hover: (draggedItem: { index: number }) => {
      if (draggedItem.index !== index) {
        requestAnimationFrame(() => {
          onSwitchProducts?.(draggedItem.index, index);
          draggedItem.index = index;
        });
      }
    },
  });

  return (
    <Box className={classes.card} {...({ ref: (node: any) => ref(drop(node)) } as any)}>
      <Box className={classes.titleContainer}>
        {logo && <img className={classes.productLogo} src={logo} alt="Proposal product logo" />}

        <Box className={classes.titleWithValidation}>
          {mode === ProposalProductCardModes.Desking && !product?.aftermarketProduct?.aftermarketVendorApiChannel && (
            <ProposalProductPricingValidationStatus isValid={productConfiguration?.isPricingValid} />
          )}

          <Box className={classes.titleWithLogo}>
            <Typography variant="h6" className="productName" style={{ fontSize: 18, lineHeight: 1.3 }}>
              {displayTitle}
            </Typography>

            {product?.aftermarketProduct?.productCategory && (
              <ProposalProductsCardTypeChip
                productCategory={product.aftermarketProduct.productCategory}
                productType={product.aftermarketProduct.productType}
              />
            )}

            <RenderWithCondition
              condition={
                !product.aftermarketProduct?.aftermarketVendorApiChannel &&
                !!product.aftermarketProduct?.partnerLinks?.length &&
                !!product.aftermarketProduct?.partnerLinks?.[0]?.partnerProfile?.name
              }
            >
              <Typography className="productName" variant="body2">
                {product.aftermarketProduct?.partnerLinks?.[0]?.partnerProfile?.name}
              </Typography>
            </RenderWithCondition>
          </Box>
        </Box>

        <Box className="actions">
          {onOpenProductDetailsDialog && (
            <IconButton
              size="small"
              onClick={event => {
                event.stopPropagation();
                onOpenProductDetailsDialog();
              }}
            >
              <InfoOutlined color="primary" />
            </IconButton>
          )}

          {onRemoveProduct && (
            <IconButton
              size="small"
              onClick={event => {
                event.stopPropagation();
                onRemoveProduct();
              }}
            >
              <DeleteOutlineIcon color="error" />
            </IconButton>
          )}
        </Box>
      </Box>

      <ProductCatalogDetails product={product} productConfiguration={productConfiguration} />

      {!!product.aftermarketProduct?.productIsNoLongerAvailable && <RemoveNoLongerAvailableProduct product={product} />}

      <ProposalProductCardDocuments product={product} />

      <CardPriceBlock
        type="simple"
        cost={productConfiguration?.cost}
        markup={productConfiguration?.markup}
        retailCost={productConfiguration?.retailCost}
      />

      {!!productConfiguration?.addons?.length && (
        <ProposalProductCardAddons onRemoveAddon={onRemoveAddon} addons={productConfiguration.addons} />
      )}
    </Box>
  );
};

const useStyles = makeStyles({
  card: {
    gap: '8px',
    display: 'flex',
    padding: '8px',
    cursor: 'grab',
    borderRadius: '2px',
    flexDirection: 'column',
    backgroundColor: grey['100'],
  },
  titleContainer: {
    gap: '8px',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  titleWithValidation: {
    gap: '4px',
    display: 'flex',
    alignItems: 'flex-start',
  },
  titleWithLogo: {
    display: 'flex',
    flexDirection: 'column',
  },
  productLogo: {
    width: '36px',
    height: '36px',
  },
});
